import dayjs from 'dayjs';
import {
  ContractorAvailabilityDto,
  ContractorAvailabilityException
} from '../models/contractor.model';
import { dayAbbreviations, DayOfWeek } from '../models/days-of-week.model';

/**
 * Maps DayOfWeek string enum values to JavaScript Date.getDay() numbers.
 * Date.getDay(): 0=Sunday, 1=Monday, 2=Tuesday, ..., 6=Saturday
 */
const DAY_TO_JS_DAY: Record<string, number> = {
  Sunday: 0,
  Monday: 1,
  Tuesday: 2,
  Wednesday: 3,
  Thursday: 4,
  Friday: 5,
  Saturday: 6
};

/**
 * Utility functions for working with contractor availability.
 */
export class AvailabilityUtils {
  /**
   * Resolves dayOfWeek to a JavaScript Date.getDay() number (0-6).
   * Handles both numeric values and string day names (DayOfWeek enum).
   */
  private static resolveDayOfWeek(
    dayOfWeek: DayOfWeek | string | number
  ): number {
    if (typeof dayOfWeek === 'number') return dayOfWeek;
    return DAY_TO_JS_DAY[dayOfWeek] ?? -1;
  }

  /**
   * Converts weekly recurring availability patterns to specific calendar dates for a given month.
   *
   * @param availabilities Array of weekly availability patterns
   * @param year The year to generate dates for
   * @param month The month to generate dates for (0-11, JavaScript Date convention)
   * @returns Array of Date objects representing available dates in the specified month
   *
   * @example
   * // If contractor is available on Mondays and Wednesdays
   * const dates = AvailabilityUtils.convertToCalendarDates(availabilities, 2025, 10);
   * // Returns all Mondays and Wednesdays in November 2025
   */
  static convertToCalendarDates(
    availabilities: ContractorAvailabilityDto[],
    year: number,
    month: number
  ): Date[] {
    if (!availabilities || availabilities.length === 0) {
      return [];
    }

    const dates: Date[] = [];
    const activeDayOfWeek = new Set(
      availabilities
        .filter((a) => a.isActive !== false)
        .map((a) => this.resolveDayOfWeek(a.dayOfWeek))
    );

    const lastDay = new Date(year, month + 1, 0);

    for (let day = 1; day <= lastDay.getDate(); day++) {
      const currentDate = new Date(year, month, day);
      const dayOfWeek = currentDate.getDay();

      if (activeDayOfWeek.has(dayOfWeek)) {
        dates.push(currentDate);
      }
    }

    return dates;
  }

  /**
   * Converts weekly recurring availability patterns to specific calendar dates
   * for days where the contractor is NOT available in a given month.
   *
   * @param availabilities Array of weekly availability patterns
   * @param year The year to generate dates for
   * @param month The month to generate dates for (0-11, JavaScript Date convention)
   * @returns Array of Date objects representing unavailable dates in the specified month
   */
  static convertToUnavailableDates(
    availabilities: ContractorAvailabilityDto[],
    year: number,
    month: number
  ): Date[] {
    const activeDayOfWeek = new Set(
      availabilities
        .filter((a) => a.isActive !== false)
        .map((a) => this.resolveDayOfWeek(a.dayOfWeek))
    );

    const dates: Date[] = [];
    const lastDay = new Date(year, month + 1, 0);

    for (let day = 1; day <= lastDay.getDate(); day++) {
      const currentDate = new Date(year, month, day);
      const dayOfWeek = currentDate.getDay();

      if (!activeDayOfWeek.has(dayOfWeek)) dates.push(currentDate);
    }

    return dates;
  }

  /**
   * Applies availability exceptions to the calendar dates.
   * - isUnavailable=true: removes the date from available, adds to unavailable
   * - isUnavailable=false: removes the date from unavailable, adds to available
   */
  static applyExceptions(
    availableDates: Date[],
    unavailableDates: Date[],
    exceptions: ContractorAvailabilityException[]
  ): { availableDates: Date[]; unavailableDates: Date[] } {
    if (!exceptions || exceptions.length === 0) {
      return { availableDates, unavailableDates };
    }

    const available = new Map<string, Date>();
    const unavailable = new Map<string, Date>();

    for (const d of availableDates)
      available.set(dayjs(d).format('YYYY-MM-DD'), d);

    for (const d of unavailableDates)
      unavailable.set(dayjs(d).format('YYYY-MM-DD'), d);

    for (const exception of exceptions) {
      const dateKey = exception.exceptionDate.slice(0, 10);
      const [y, m, d] = dateKey.split('-').map(Number);
      const dateObj = new Date(y, m - 1, d);

      if (exception.isUnavailable) {
        available.delete(dateKey);
        unavailable.set(dateKey, dateObj);
      } else {
        unavailable.delete(dateKey);
        available.set(dateKey, dateObj);
      }
    }

    return {
      availableDates: Array.from(available.values()),
      unavailableDates: Array.from(unavailable.values())
    };
  }

  /**
   * Gets the display label for a day of week.
   *
   * @param dayOfWeek The DayOfWeek enum value
   * @returns The display name (e.g., "Monday", "Tuesday")
   */
  static getDayOfWeekLabel(dayOfWeek: DayOfWeek): string {
    const labels: Record<DayOfWeek, string> = {
      [DayOfWeek.Sunday]: 'Sunday',
      [DayOfWeek.Monday]: 'Monday',
      [DayOfWeek.Tuesday]: 'Tuesday',
      [DayOfWeek.Wednesday]: 'Wednesday',
      [DayOfWeek.Thursday]: 'Thursday',
      [DayOfWeek.Friday]: 'Friday',
      [DayOfWeek.Saturday]: 'Saturday'
    };
    return labels[dayOfWeek] || '';
  }

  /**
   * Validates and formats a time string.
   *
   * Accepts HH:mm or HH:mm:ss input and returns either:
   * - 12h: h:mm AM/PM (default, backward compatible)
   * - 24h: hh:mm
   * Invalid values return an empty string.
   */
  static formatTime(timeString: string, output: '12h' | '24h' = '12h'): string {
    if (!timeString) return '';

    const match = /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/.exec(timeString.trim());
    if (!match) return '';

    const hours = Number(match[1]);
    const minutes = Number(match[2]);
    const seconds = match[3] === undefined ? 0 : Number(match[3]);

    if (
      Number.isNaN(hours) ||
      Number.isNaN(minutes) ||
      Number.isNaN(seconds) ||
      hours < 0 ||
      hours > 23 ||
      minutes < 0 ||
      minutes > 59 ||
      seconds < 0 ||
      seconds > 59
    ) {
      return '';
    }

    if (output === '24h') {
      return `${hours.toString().padStart(2, '0')}:${minutes
        .toString()
        .padStart(2, '0')}`;
    }

    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    return `${displayHours}:${minutes.toString().padStart(2, '0')} ${period}`;
  }

  /**
   * Gets a summary of availability for display.
   *
   * @param availabilities Array of availability records
   * @returns Formatted summary string (e.g., "Mon, Wed, Fri")
   */
  static getAvailabilitySummary(
    availabilities: ContractorAvailabilityDto[]
  ): string {
    if (!availabilities || availabilities.length === 0)
      return 'No availability set';

    const activeAvailabilities = availabilities.filter(
      (a) => a.isActive !== false
    );
    if (activeAvailabilities.length === 0) return 'No availability set';

    const days = activeAvailabilities
      .map((a) => dayAbbreviations[a.dayOfWeek])
      .join(', ');

    return days;
  }
}
